import moment from 'moment';
import { getIntercomAppId } from '../config';
import { createDeferredLoader, loadScript } from './deferredLoader';
import type { AnalyticsGroup, AnalyticsIdentity, AnalyticsProvider } from './types';

const INTERCOM_PROVIDER_NAME = 'Intercom';
const INTERCOM_SCRIPT_ID = 'intercom-messenger';
const INTERCOM_API_BASE = 'https://api-iam.intercom.io';

/**
 * Intercom caps how many distinct event names a workspace accepts and is a support tool rather than an
 * analytics store, so events reach it only by explicit opt-in.
 */
export const INTERCOM_TRACKED_EVENT_NAMES: ReadonlySet<string> = new Set<string>([]);

const loader = createDeferredLoader({ name: INTERCOM_PROVIDER_NAME });
let booted = false;

const withMessenger = (action: (messenger: IntercomClient) => void) =>
  loader.enqueue(() => {
    const messenger = window.Intercom;
    if (messenger) {
      action(messenger);
    }
  });

/** Every command other than boot is ignored by an unbooted messenger, so they are not worth sending. */
const withBootedMessenger = (action: (messenger: IntercomClient) => void) =>
  booted ? withMessenger(action) : Promise.resolve();

const readString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const toUnixSeconds = (value: unknown): number | undefined => {
  const date = readString(value);
  if (date === undefined) {
    return undefined;
  }

  const parsed = moment.utc(date);
  return parsed.isValid() ? parsed.unix() : undefined;
};

const toFullName = (traits: Record<string, unknown>): string | undefined => {
  const name = [readString(traits.firstName), readString(traits.lastName)].filter(Boolean).join(' ');
  return name === '' ? undefined : name;
};

const toMessengerUser = ({ userId, traits, intercomUserHash }: AnalyticsIdentity): Record<string, unknown> => ({
  user_id: userId,
  user_hash: intercomUserHash,
  email: readString(traits.email),
  name: toFullName(traits),
  created_at: toUnixSeconds(traits.createdAt),
  account_tier: readString(traits.accountTier),
  trial_status: readString(traits.trialStatus),
});

const toMessengerCompany = ({ groupId, traits }: AnalyticsGroup): Record<string, unknown> => ({
  company_id: groupId,
  name: readString(traits.name),
  account_tier: readString(traits.accountTier),
});

/** Intercom rejects nested metadata, so anything structured is dropped rather than sent as "[object Object]". */
const toFlatMetadata = (properties: Record<string, unknown> | undefined): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(properties ?? {}).filter(
      ([, value]) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean',
    ),
  );

export const intercomProvider: AnalyticsProvider = {
  name: INTERCOM_PROVIDER_NAME,

  load: () => {
    const appId = getIntercomAppId();
    if (!appId) {
      return;
    }

    loader.start(() => loadScript({ id: INTERCOM_SCRIPT_ID, src: `https://widget.intercom.io/widget/${appId}` }));
  },

  /**
   * The messenger is booted here rather than on load because identity verification rejects a session whose
   * user_hash arrives after boot.
   */
  identify: (identity) => {
    // Pre-account signup identifies an anonymous uuid, which identity verification would reject.
    if (!identity.intercomUserHash) {
      return;
    }

    withMessenger((messenger) => {
      const settings = {
        app_id: getIntercomAppId(),
        api_base: INTERCOM_API_BASE,
        ...toMessengerUser(identity),
      };
      messenger(booted ? 'update' : 'boot', settings);
      booted = true;
    });
  },

  group: (group) => withBootedMessenger((messenger) => messenger('update', { company: toMessengerCompany(group) })),

  /** An update on navigation is what makes the messenger re-check for messages targeted at the new page. */
  page: () => withBootedMessenger((messenger) => messenger('update')),

  track: ({ name, properties }) => {
    if (!INTERCOM_TRACKED_EVENT_NAMES.has(name)) {
      return;
    }
    withBootedMessenger((messenger) => messenger('trackEvent', name, toFlatMetadata(properties)));
  },

  reset: () => {
    const wasBooted = booted;
    booted = false;
    return wasBooted ? withMessenger((messenger) => messenger('shutdown')) : Promise.resolve();
  },
};
